import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n.js";
import { listTimezones } from "../../services/settings.api";
import useSettings from "../../hooks/useSettings";
import api from "../../services/api";
import { refreshAccessSnapshot } from "../../context/AccessControl/accessSnapshot";
import {
  DEFAULT_TICKET_CLOSE_MESSAGE,
  DEFAULT_REOPEN_GRACE_MESSAGE,
} from "../../constants/defaultTicketCloseMessage";
import {
  DEFAULT_USER_RATING_MESSAGE_TEXT,
  DEFAULT_SEND_GREETING_ACCEPTED_MESSAGE_TEXT,
  DEFAULT_TRANSFER_TICKET_MESSAGE_TEXT,
  DEFAULT_SEND_GREETING_ONE_QUEUE_MESSAGE_TEXT
} from "../../constants/defaultMessagingSettings";

// D8.5.3 — Lógica (48 estados + carga + handlers) de Settings/Options extraída del god file.
export default function useOptionsSettings(props) {
  const { settings, scheduleTypeChanged } = props;
  const [userRating, setUserRating] = useState("disabled");
  // Modo equipos (doc 0380 D7 · paso 2.A). Es el MISMO interruptor que gobierna
  // el alcance de equipo en el motor: un solo hecho de negocio, una sola llave.
  const [teamsEnabled, setTeamsEnabled] = useState("disabled");
  const [loadingTeamsEnabled, setLoadingTeamsEnabled] = useState(false);
  const [scheduleType, setScheduleType] = useState("disabled");
  const [callType, setCallType] = useState("enabled");
  const [chatbotType, setChatbotType] = useState("");
  const [CheckMsgIsGroup, setCheckMsgIsGroupType] = useState("enabled");
  const [callAutoMessage, setCallAutoMessage] = useState("");
  const [savedCallAutoMessage, setSavedCallAutoMessage] = useState(""); // ✅ Valor guardado en BD

  const [loadingUserRating, setLoadingUserRating] = useState(false);
  const [loadingScheduleType, setLoadingScheduleType] = useState(false);
      const [loadingCallType, setLoadingCallType] = useState(false);
  const [loadingChatbotType, setLoadingChatbotType] = useState(false);
  const [loadingCheckMsgIsGroup, setLoadingCheckMsgIsGroup] = useState(false);
  const [loadingCallAutoMessage, setLoadingCallAutoMessage] = useState(false);
 
  // Configuración de zona horaria para campañas
  const [timezoneType, setTimezoneType] = useState(""); // ✅ Inicializar vacío para evitar warning
  const [loadingTimezoneType, setLoadingTimezoneType] = useState(false);
  const [timezones, setTimezones] = useState([]); // ✅ DB-FIRST: Lista dinámica de timezones
  const [loadingTimezones, setLoadingTimezones] = useState(true);
  
  const [SendGreetingAccepted, setSendGreetingAccepted] = useState("disabled");
  const [loadingSendGreetingAccepted, setLoadingSendGreetingAccepted] = useState(false);
  
  const [SettingsTransfTicket, setSettingsTransfTicket] = useState("disabled");
  const [loadingSettingsTransfTicket, setLoadingSettingsTransfTicket] = useState(false);
  
  const [sendGreetingMessageOneQueues, setSendGreetingMessageOneQueues] = useState("disabled");
  const [loadingSendGreetingMessageOneQueues, setLoadingSendGreetingMessageOneQueues] = useState(false);

  // ✅ NUEVO: Configuración de Firmas Internas
  const [internalSignature, setInternalSignature] = useState("disabled");
  const [loadingInternalSignature, setLoadingInternalSignature] = useState(false);

  // ✅ NUEVO: Configuración de Validación de Números de WhatsApp
  const [validateWhatsAppNumbers, setValidateWhatsAppNumbers] = useState("disabled");
  const [loadingValidateWhatsAppNumbers, setLoadingValidateWhatsAppNumbers] = useState(false);

  const [removeTagsOnCloseTicket, setRemoveTagsOnCloseTicket] = useState("disabled");
  const [loadingRemoveTagsOnCloseTicket, setLoadingRemoveTagsOnCloseTicket] = useState(false);

  const [reopenTicketToBot, setReopenTicketToBot] = useState("disabled");
  const [loadingReopenTicketToBot, setLoadingReopenTicketToBot] = useState(false);
  const [reopenTicketToBotMinutes, setReopenTicketToBotMinutes] = useState("0");
  const [savedReopenTicketToBotMinutes, setSavedReopenTicketToBotMinutes] = useState("0");
  const [loadingReopenTicketToBotMinutes, setLoadingReopenTicketToBotMinutes] = useState(false);
  // Sin fila en BD el backend usa el texto por defecto: la pantalla muestra lo mismo.
  const [reopenGraceMessageText, setReopenGraceMessageText] = useState(DEFAULT_REOPEN_GRACE_MESSAGE);
  const [savedReopenGraceMessageText, setSavedReopenGraceMessageText] = useState(DEFAULT_REOPEN_GRACE_MESSAGE);
  const [loadingReopenGraceMessageText, setLoadingReopenGraceMessageText] = useState(false);

  const [customerMessageLocale, setCustomerMessageLocale] = useState("es");
  const [loadingCustomerMessageLocale, setLoadingCustomerMessageLocale] = useState(false);

  const [sendTicketCloseMessage, setSendTicketCloseMessage] = useState("disabled");
  const [loadingSendTicketCloseMessage, setLoadingSendTicketCloseMessage] = useState(false);
  const [ticketCloseMessageText, setTicketCloseMessageText] = useState("");
  const [savedTicketCloseMessageText, setSavedTicketCloseMessageText] = useState("");
  const [loadingTicketCloseMessageText, setLoadingTicketCloseMessageText] = useState(false);

  const [userRatingMessageText, setUserRatingMessageText] = useState("");
  const [savedUserRatingMessageText, setSavedUserRatingMessageText] = useState("");
  const [loadingUserRatingMessageText, setLoadingUserRatingMessageText] = useState(false);

  const [sendGreetingAcceptedMessageText, setSendGreetingAcceptedMessageText] = useState("");
  const [savedSendGreetingAcceptedMessageText, setSavedSendGreetingAcceptedMessageText] = useState("");
  const [loadingSendGreetingAcceptedMessageText, setLoadingSendGreetingAcceptedMessageText] = useState(false);

  const [transferTicketMessageText, setTransferTicketMessageText] = useState("");
  const [savedTransferTicketMessageText, setSavedTransferTicketMessageText] = useState("");
  const [loadingTransferTicketMessageText, setLoadingTransferTicketMessageText] = useState(false);

  const [greetingOneQueueMessageText, setGreetingOneQueueMessageText] = useState("");
  const [savedGreetingOneQueueMessageText, setSavedGreetingOneQueueMessageText] = useState("");
  const [loadingGreetingOneQueueMessageText, setLoadingGreetingOneQueueMessageText] = useState(false);

  const { update } = useSettings();

  useEffect(() => {
    if (Array.isArray(settings) && settings.length) {
      const rbacScope = settings.find((s) => s.key === "rbacScopeEnabled");
      if (rbacScope) setTeamsEnabled(rbacScope.value);

      const userRating = settings.find((s) => s.key === "userRating");
      if (userRating) {
        setUserRating(userRating.value);
      }
      const scheduleType = settings.find((s) => s.key === "scheduleType");
      if (scheduleType) {
        setScheduleType(scheduleType.value);
      }
      const callType = settings.find((s) => s.key === "call");
      if (callType) {
        setCallType(callType.value);
      }
      const callAutoMessageSetting = settings.find((s) => s.key === "callAutoMessage");
      if (callAutoMessageSetting) {
        setCallAutoMessage(callAutoMessageSetting.value);
        setSavedCallAutoMessage(callAutoMessageSetting.value); // ✅ Guardar valor original de BD
      } else {
        // Si no existe, usar valor por defecto
        const defaultMessage = "*Mensaje Automático:*\n\nLas llamadas de voz y video están deshabilitadas para este WhatsApp, favor enviar un mensaje de texto. Gracias";
        setCallAutoMessage(defaultMessage);
        setSavedCallAutoMessage(defaultMessage);
      }
      const CheckMsgIsGroup = settings.find((s) => s.key === "CheckMsgIsGroup");
      if (CheckMsgIsGroup) {
        setCheckMsgIsGroupType(CheckMsgIsGroup.value);
      }

      const SendGreetingAccepted = settings.find((s) => s.key === "sendGreetingAccepted");
      if (SendGreetingAccepted) {
        setSendGreetingAccepted(SendGreetingAccepted.value);
      }
	  
	  const SettingsTransfTicket = settings.find((s) => s.key === "sendMsgTransfTicket");
      if (SettingsTransfTicket) {
        setSettingsTransfTicket(SettingsTransfTicket.value);
      }

      const sendGreetingMessageOneQueues = settings.find((s) => s.key === "sendGreetingMessageOneQueues");
      if (sendGreetingMessageOneQueues) {
        setSendGreetingMessageOneQueues(sendGreetingMessageOneQueues.value)
      }

      // ✅ NUEVO: Cargar configuración de Firmas Internas
      const internalSignatureSetting = settings.find((s) => s.key === "internalSignature");
      if (internalSignatureSetting) {
        setInternalSignature(internalSignatureSetting.value);
      }

      // ✅ NUEVO: Cargar configuración de Validación de Números de WhatsApp
      const validateWhatsAppNumbersSetting = settings.find((s) => s.key === "validateWhatsAppNumbers");
      if (validateWhatsAppNumbersSetting) {
        setValidateWhatsAppNumbers(validateWhatsAppNumbersSetting.value);
      } else {
        // Valor por defecto: disabled
        setValidateWhatsAppNumbers("disabled");
      }

      const removeTagsOnCloseSetting = settings.find((s) => s.key === "removeTagsOnCloseTicket");
      if (removeTagsOnCloseSetting) {
        setRemoveTagsOnCloseTicket(removeTagsOnCloseSetting.value);
      }

      const reopenToBotSetting = settings.find((s) => s.key === "reopenTicketToBot");
      if (reopenToBotSetting) {
        setReopenTicketToBot(reopenToBotSetting.value);
      }

      const reopenToBotMinutesSetting = settings.find((s) => s.key === "reopenTicketToBotMinutes");
      if (reopenToBotMinutesSetting) {
        setReopenTicketToBotMinutes(reopenToBotMinutesSetting.value);
        setSavedReopenTicketToBotMinutes(reopenToBotMinutesSetting.value);
      }

      const reopenGraceTextSetting = settings.find((s) => s.key === "reopenGraceMessageText");
      if (reopenGraceTextSetting) {
        setReopenGraceMessageText(reopenGraceTextSetting.value || "");
        setSavedReopenGraceMessageText(reopenGraceTextSetting.value || "");
      }

      const cmLocale = settings.find((s) => s.key === "customerMessageLocale");
      if (cmLocale && ["es", "en", "pt"].includes(cmLocale.value)) {
        setCustomerMessageLocale(cmLocale.value);
      } else {
        setCustomerMessageLocale("es");
      }

      const sendTicketCloseMsgSetting = settings.find((s) => s.key === "sendTicketCloseMessage");
      if (sendTicketCloseMsgSetting) {
        setSendTicketCloseMessage(sendTicketCloseMsgSetting.value);
      }

      const ticketCloseMessageTextSetting = settings.find((s) => s.key === "ticketCloseMessageText");
      if (
        ticketCloseMessageTextSetting &&
        String(ticketCloseMessageTextSetting.value || "").trim()
      ) {
        setTicketCloseMessageText(ticketCloseMessageTextSetting.value);
        setSavedTicketCloseMessageText(ticketCloseMessageTextSetting.value);
      } else {
        setTicketCloseMessageText(DEFAULT_TICKET_CLOSE_MESSAGE);
        setSavedTicketCloseMessageText(DEFAULT_TICKET_CLOSE_MESSAGE);
      }

      const chatbotType = settings.find((s) => s.key === "chatBotType");
      if (chatbotType) {
        setChatbotType(chatbotType.value);
      }

      const userRatingMsg = settings.find((s) => s.key === "userRatingMessageText");
      if (userRatingMsg && String(userRatingMsg.value || "").trim()) {
        setUserRatingMessageText(userRatingMsg.value);
        setSavedUserRatingMessageText(userRatingMsg.value);
      } else {
        setUserRatingMessageText(DEFAULT_USER_RATING_MESSAGE_TEXT);
        setSavedUserRatingMessageText(DEFAULT_USER_RATING_MESSAGE_TEXT);
      }

      const greetingAcceptedMsg = settings.find((s) => s.key === "sendGreetingAcceptedMessageText");
      if (greetingAcceptedMsg && String(greetingAcceptedMsg.value || "").trim()) {
        setSendGreetingAcceptedMessageText(greetingAcceptedMsg.value);
        setSavedSendGreetingAcceptedMessageText(greetingAcceptedMsg.value);
      } else {
        setSendGreetingAcceptedMessageText(DEFAULT_SEND_GREETING_ACCEPTED_MESSAGE_TEXT);
        setSavedSendGreetingAcceptedMessageText(DEFAULT_SEND_GREETING_ACCEPTED_MESSAGE_TEXT);
      }

      const transferMsg = settings.find((s) => s.key === "transferTicketMessageText");
      if (transferMsg && String(transferMsg.value || "").trim()) {
        setTransferTicketMessageText(transferMsg.value);
        setSavedTransferTicketMessageText(transferMsg.value);
      } else {
        setTransferTicketMessageText(DEFAULT_TRANSFER_TICKET_MESSAGE_TEXT);
        setSavedTransferTicketMessageText(DEFAULT_TRANSFER_TICKET_MESSAGE_TEXT);
      }

      const oneQueueMsg = settings.find((s) => s.key === "sendGreetingMessageOneQueuesText");
      if (oneQueueMsg && String(oneQueueMsg.value || "").trim()) {
        setGreetingOneQueueMessageText(oneQueueMsg.value);
        setSavedGreetingOneQueueMessageText(oneQueueMsg.value);
      } else {
        setGreetingOneQueueMessageText(DEFAULT_SEND_GREETING_ONE_QUEUE_MESSAGE_TEXT);
        setSavedGreetingOneQueueMessageText(DEFAULT_SEND_GREETING_ONE_QUEUE_MESSAGE_TEXT);
      }

      const timezoneType = settings.find((s) => s.key === "timezone");
      if (timezoneType && timezoneType.value) {
        // ✅ Guardar en cache, pero NO establecer todavía (se establecerá cuando los timezones se carguen)
        localStorage.setItem("campaignTimezone", timezoneType.value);
      } else {
        // ✅ CORRECCIÓN: Usar fallback unificado (America/Lima, compatible con install.sh)
        const defaultTimezone = "America/Lima";
        localStorage.setItem("campaignTimezone", defaultTimezone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  // ✅ DB-FIRST: Cargar timezones dinámicamente desde la base de datos
  useEffect(() => {
    const loadTimezones = async () => {
      try {
        setLoadingTimezones(true);
        const { data } = await listTimezones();
        if (data && data.success && data.data) {
          setTimezones(data.data);
          
          // ✅ CORRECCIÓN: Establecer timezoneType SOLO después de cargar timezones
          // Esto evita el warning de Material-UI de "out-of-range value"
          const savedTimezone = localStorage.getItem("campaignTimezone") || "America/Lima";
          // Verificar que el timezone guardado existe en la lista cargada
          const exists = data.data.some(tz => tz.timezone === savedTimezone);
          const finalTimezone = exists ? savedTimezone : "America/Lima";
          setTimezoneType(finalTimezone);
          // Actualizar localStorage con el timezone válido
          localStorage.setItem("campaignTimezone", finalTimezone);
        } else {
          // Fallback: Si falla, usar lista hardcodeada mínima
          console.warn('No se pudieron cargar timezones desde BD, usando fallback');
          setTimezones([
            { timezone: 'America/Lima', displayName: 'Perú (America/Lima)', region: 'America' }
          ]);
          if (!timezoneType || timezoneType === "") {
            setTimezoneType("America/Lima");
          }
        }
      } catch (error) {
        console.error('Error cargando timezones:', error);
        // Fallback: Lista mínima
        setTimezones([
          { timezone: 'America/Lima', displayName: 'Perú (America/Lima)', region: 'America' }
        ]);
        if (!timezoneType || timezoneType === "") {
          setTimezoneType("America/Lima");
        }
      } finally {
        setLoadingTimezones(false);
      }
    };
    
    loadTimezones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCustomerMessageLocale(value) {
    setCustomerMessageLocale(value);
    setLoadingCustomerMessageLocale(true);
    try {
      await update({
        key: "customerMessageLocale",
        value,
      });
      toast.success(i18n.t("settings.success"));
    } catch (e) {
      toast.error(i18n.t("settings.options.callAutoMessageSaveError"));
    } finally {
      setLoadingCustomerMessageLocale(false);
    }
  }

  /**
   * Enciende o apaga el trabajo por equipos.
   *
   * Tras guardar recarga el snapshot de acceso: el menú lateral decide con él
   * si muestra Subagrupaciones, y sin recargarlo el cambio no se vería hasta
   * volver a entrar.
   */
  async function handleChangeTeamsEnabled(value) {
    setTeamsEnabled(value);
    setLoadingTeamsEnabled(true);
    try {
      await update({ key: "rbacScopeEnabled", value });
      await refreshAccessSnapshot(api);
      toast.success(i18n.t("settings.options.teamsEnabledSaved"));
    } catch (err) {
      toast.error(i18n.t("settings.options.teamsEnabledError"));
    } finally {
      setLoadingTeamsEnabled(false);
    }
  }

  async function handleChangeUserRating(value) {
    setUserRating(value);
    setLoadingUserRating(true);
    try {
      await update({
        key: "userRating",
        value,
      });
      if (value === "enabled") {
        const raw =
          String(userRatingMessageText || "").trim() ||
          String(savedUserRatingMessageText || "").trim();
        if (!raw) {
          await update({
            key: "userRatingMessageText",
            value: DEFAULT_USER_RATING_MESSAGE_TEXT,
          });
          setUserRatingMessageText(DEFAULT_USER_RATING_MESSAGE_TEXT);
          setSavedUserRatingMessageText(DEFAULT_USER_RATING_MESSAGE_TEXT);
        }
      }
      toast.success(i18n.t("settings.success"));
    } catch (e) {
      toast.error(i18n.t("settings.options.callAutoMessageSaveError"));
    } finally {
      setLoadingUserRating(false);
    }
  }

  async function handleSendGreetingMessageOneQueues(value) {
    setSendGreetingMessageOneQueues(value);
    setLoadingSendGreetingMessageOneQueues(true);
    try {
      await update({
        key: "sendGreetingMessageOneQueues",
        value,
      });
      if (value === "enabled") {
        const raw =
          String(greetingOneQueueMessageText || "").trim() ||
          String(savedGreetingOneQueueMessageText || "").trim();
        if (!raw) {
          await update({
            key: "sendGreetingMessageOneQueuesText",
            value: DEFAULT_SEND_GREETING_ONE_QUEUE_MESSAGE_TEXT,
          });
          setGreetingOneQueueMessageText(DEFAULT_SEND_GREETING_ONE_QUEUE_MESSAGE_TEXT);
          setSavedGreetingOneQueueMessageText(DEFAULT_SEND_GREETING_ONE_QUEUE_MESSAGE_TEXT);
        }
      }
      toast.success(i18n.t("settings.success"));
    } catch (e) {
      toast.error(i18n.t("settings.options.callAutoMessageSaveError"));
    } finally {
      setLoadingSendGreetingMessageOneQueues(false);
    }
  }

  // ✅ NUEVO: Función para manejar cambio de Firmas Internas
  async function handleInternalSignature(value) {
    setInternalSignature(value);
    setLoadingInternalSignature(true);
    await update({
      key: "internalSignature",
      value,
    });
    toast.success(i18n.t("settings.success"));
    setLoadingInternalSignature(false);
  }

  // ✅ NUEVO: Función para manejar cambio de Validación de Números de WhatsApp
  async function handleValidateWhatsAppNumbers(value) {
    setValidateWhatsAppNumbers(value);
    setLoadingValidateWhatsAppNumbers(true);
    await update({
      key: "validateWhatsAppNumbers",
      value,
    });
    toast.success(i18n.t("settings.success"));
    setLoadingValidateWhatsAppNumbers(false);
  }

  async function handleScheduleType(value) {
    setScheduleType(value);
    setLoadingScheduleType(true);
    await update({
      key: "scheduleType",
      value,
    });
    toast.success(i18n.t("settings.success"));
    setLoadingScheduleType(false);
    scheduleTypeChanged(value);
  }

  async function handleCallType(value) {
    setCallType(value);
    setLoadingCallType(true);
    await update({
      key: "call",
      value,
    });
    toast.success(i18n.t("settings.success"));
    setLoadingCallType(false);
  }

  async function handleCallAutoMessage(value) {
    if (value.length > 500) {
      toast.error(i18n.t("settings.options.callAutoMessageMaxLength"));
      return;
    }
    setLoadingCallAutoMessage(true);
    try {
      await update({
        key: "callAutoMessage",
        value,
      });
      setCallAutoMessage(value);
      setSavedCallAutoMessage(value); // ✅ Actualizar valor guardado después de éxito
      toast.success(i18n.t("settings.success"));
    } catch (error) {
      toast.error(i18n.t("settings.options.callAutoMessageSaveError"));
      console.error("Error guardando callAutoMessage:", error);
      setCallAutoMessage(savedCallAutoMessage); // ✅ Restaurar valor anterior si falla
    } finally {
      setLoadingCallAutoMessage(false);
    }
  }

  function handleRestoreDefaultMessage() {
    const defaultMessage = "*Mensaje Automático:*\n\nLas llamadas de voz y video están deshabilitadas para este WhatsApp, favor enviar un mensaje de texto. Gracias";
    handleCallAutoMessage(defaultMessage);
  }

  async function handleChatbotType(value) {
    setChatbotType(value);
    setLoadingChatbotType(true);
    await update({
      key: "chatBotType",
      value,
    });
    toast.success(i18n.t("settings.success"));
    setLoadingChatbotType(false);
  }

  async function handleGroupType(value) {
    setCheckMsgIsGroupType(value);
    setLoadingCheckMsgIsGroup(true);
    await update({
      key: "CheckMsgIsGroup",
      value,
    });
    toast.success(i18n.t("settings.success"));
    setLoadingCheckMsgIsGroup(false);
  }
  
  async function handleSendGreetingAccepted(value) {
    setSendGreetingAccepted(value);
    setLoadingSendGreetingAccepted(true);
    try {
      await update({
        key: "sendGreetingAccepted",
        value,
      });
      if (value === "enabled") {
        const raw =
          String(sendGreetingAcceptedMessageText || "").trim() ||
          String(savedSendGreetingAcceptedMessageText || "").trim();
        if (!raw) {
          await update({
            key: "sendGreetingAcceptedMessageText",
            value: DEFAULT_SEND_GREETING_ACCEPTED_MESSAGE_TEXT,
          });
          setSendGreetingAcceptedMessageText(DEFAULT_SEND_GREETING_ACCEPTED_MESSAGE_TEXT);
          setSavedSendGreetingAcceptedMessageText(DEFAULT_SEND_GREETING_ACCEPTED_MESSAGE_TEXT);
        }
      }
      toast.success(i18n.t("settings.success"));
    } catch (e) {
      toast.error(i18n.t("settings.options.callAutoMessageSaveError"));
    } finally {
      setLoadingSendGreetingAccepted(false);
    }
  }  

  async function handleSettingsTransfTicket(value) {
    setSettingsTransfTicket(value);
    setLoadingSettingsTransfTicket(true);
    try {
      await update({
        key: "sendMsgTransfTicket",
        value,
      });
      if (value === "enabled") {
        const raw =
          String(transferTicketMessageText || "").trim() ||
          String(savedTransferTicketMessageText || "").trim();
        if (!raw) {
          await update({
            key: "transferTicketMessageText",
            value: DEFAULT_TRANSFER_TICKET_MESSAGE_TEXT,
          });
          setTransferTicketMessageText(DEFAULT_TRANSFER_TICKET_MESSAGE_TEXT);
          setSavedTransferTicketMessageText(DEFAULT_TRANSFER_TICKET_MESSAGE_TEXT);
        }
      }
      toast.success(i18n.t("settings.success"));
    } catch (e) {
      toast.error(i18n.t("settings.options.callAutoMessageSaveError"));
    } finally {
      setLoadingSettingsTransfTicket(false);
    }
  }

  async function handleRemoveTagsOnCloseTicket(value) {
    setRemoveTagsOnCloseTicket(value);
    setLoadingRemoveTagsOnCloseTicket(true);
    await update({
      key: "removeTagsOnCloseTicket",
      value,
    });
    toast.success(i18n.t("settings.success"));
    setLoadingRemoveTagsOnCloseTicket(false);
  }

  async function handleReopenTicketToBot(value) {
    setReopenTicketToBot(value);
    setLoadingReopenTicketToBot(true);
    await update({
      key: "reopenTicketToBot",
      value,
    });
    toast.success(i18n.t("settings.success"));
    setLoadingReopenTicketToBot(false);
  }

  // Aviso al contacto que escribe dentro de la ventana de gracia. Vacío = no se envía.
  async function persistReopenGraceMessageText(raw) {
    const value = String(raw ?? "").slice(0, 500);
    if (value === savedReopenGraceMessageText) return;
    setLoadingReopenGraceMessageText(true);
    try {
      await update({ key: "reopenGraceMessageText", value });
      setSavedReopenGraceMessageText(value);
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      setReopenGraceMessageText(savedReopenGraceMessageText);
      toast.error(i18n.t("settings.options.reopenGraceMessageError"));
    } finally {
      setLoadingReopenGraceMessageText(false);
    }
  }

  function handleRestoreDefaultReopenGraceMessage() {
    setReopenGraceMessageText(DEFAULT_REOPEN_GRACE_MESSAGE);
    persistReopenGraceMessageText(DEFAULT_REOPEN_GRACE_MESSAGE);
  }

  // Ventana de gracia: minutos desde el cierre durante los que el ticket reabierto
  // vuelve a su departamento; pasado ese tiempo vuelve al bot. 0 = bot inmediato.
  async function persistReopenTicketToBotMinutes(raw) {
    const n = parseInt(String(raw).trim(), 10);
    const value = String(Number.isFinite(n) && n > 0 ? Math.min(n, 1440) : 0);
    setReopenTicketToBotMinutes(value);
    if (value === savedReopenTicketToBotMinutes) return;
    setLoadingReopenTicketToBotMinutes(true);
    try {
      await update({ key: "reopenTicketToBotMinutes", value });
      setSavedReopenTicketToBotMinutes(value);
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      setReopenTicketToBotMinutes(savedReopenTicketToBotMinutes);
      toast.error(i18n.t("settings.options.reopenTicketToBotMinutesError"));
    } finally {
      setLoadingReopenTicketToBotMinutes(false);
    }
  }

  async function handleSendTicketCloseMessage(value) {
    setSendTicketCloseMessage(value);
    setLoadingSendTicketCloseMessage(true);
    try {
      await update({
        key: "sendTicketCloseMessage",
        value,
      });
      if (value === "enabled") {
        const raw =
          String(ticketCloseMessageText || "").trim() ||
          String(savedTicketCloseMessageText || "").trim();
        if (!raw) {
          await update({
            key: "ticketCloseMessageText",
            value: DEFAULT_TICKET_CLOSE_MESSAGE,
          });
          setTicketCloseMessageText(DEFAULT_TICKET_CLOSE_MESSAGE);
          setSavedTicketCloseMessageText(DEFAULT_TICKET_CLOSE_MESSAGE);
        }
      }
      toast.success(i18n.t("settings.success"));
    } catch (error) {
      toast.error(i18n.t("settings.options.callAutoMessageSaveError"));
      console.error("Error guardando sendTicketCloseMessage:", error);
    } finally {
      setLoadingSendTicketCloseMessage(false);
    }
  }

  async function persistTicketCloseMessageText(value) {
    const trimmed = String(value ?? "").trim();
    const toSave = trimmed || DEFAULT_TICKET_CLOSE_MESSAGE;
    if (String(value ?? "").length > 500) {
      toast.error(i18n.t("settings.options.callAutoMessageMaxLength"));
      setTicketCloseMessageText(
        savedTicketCloseMessageText || DEFAULT_TICKET_CLOSE_MESSAGE
      );
      return;
    }
    if (toSave.length > 500) {
      toast.error(i18n.t("settings.options.callAutoMessageMaxLength"));
      return;
    }
    setLoadingTicketCloseMessageText(true);
    try {
      await update({
        key: "ticketCloseMessageText",
        value: toSave,
      });
      setTicketCloseMessageText(toSave);
      setSavedTicketCloseMessageText(toSave);
      toast.success(i18n.t("settings.success"));
    } catch (error) {
      toast.error(i18n.t("settings.options.callAutoMessageSaveError"));
      console.error("Error guardando ticketCloseMessageText:", error);
      setTicketCloseMessageText(
        savedTicketCloseMessageText || DEFAULT_TICKET_CLOSE_MESSAGE
      );
    } finally {
      setLoadingTicketCloseMessageText(false);
    }
  }

  function handleRestoreDefaultTicketCloseMessage() {
    persistTicketCloseMessageText(DEFAULT_TICKET_CLOSE_MESSAGE);
  }

  async function persistUserRatingMessageText(value) {
    const trimmed = String(value ?? "").trim();
    const toSave = trimmed || DEFAULT_USER_RATING_MESSAGE_TEXT;
    if (String(value ?? "").length > 500) {
      toast.error(i18n.t("settings.options.callAutoMessageMaxLength"));
      setUserRatingMessageText(savedUserRatingMessageText || DEFAULT_USER_RATING_MESSAGE_TEXT);
      return;
    }
    setLoadingUserRatingMessageText(true);
    try {
      await update({ key: "userRatingMessageText", value: toSave });
      setUserRatingMessageText(toSave);
      setSavedUserRatingMessageText(toSave);
      toast.success(i18n.t("settings.success"));
    } catch (e) {
      toast.error(i18n.t("settings.options.callAutoMessageSaveError"));
      setUserRatingMessageText(savedUserRatingMessageText || DEFAULT_USER_RATING_MESSAGE_TEXT);
    } finally {
      setLoadingUserRatingMessageText(false);
    }
  }

  async function persistSendGreetingAcceptedMessageText(value) {
    const trimmed = String(value ?? "").trim();
    const toSave = trimmed || DEFAULT_SEND_GREETING_ACCEPTED_MESSAGE_TEXT;
    if (String(value ?? "").length > 500) {
      toast.error(i18n.t("settings.options.callAutoMessageMaxLength"));
      setSendGreetingAcceptedMessageText(
        savedSendGreetingAcceptedMessageText || DEFAULT_SEND_GREETING_ACCEPTED_MESSAGE_TEXT
      );
      return;
    }
    setLoadingSendGreetingAcceptedMessageText(true);
    try {
      await update({ key: "sendGreetingAcceptedMessageText", value: toSave });
      setSendGreetingAcceptedMessageText(toSave);
      setSavedSendGreetingAcceptedMessageText(toSave);
      toast.success(i18n.t("settings.success"));
    } catch (e) {
      toast.error(i18n.t("settings.options.callAutoMessageSaveError"));
      setSendGreetingAcceptedMessageText(
        savedSendGreetingAcceptedMessageText || DEFAULT_SEND_GREETING_ACCEPTED_MESSAGE_TEXT
      );
    } finally {
      setLoadingSendGreetingAcceptedMessageText(false);
    }
  }

  async function persistTransferTicketMessageText(value) {
    const trimmed = String(value ?? "").trim();
    const toSave = trimmed || DEFAULT_TRANSFER_TICKET_MESSAGE_TEXT;
    if (String(value ?? "").length > 500) {
      toast.error(i18n.t("settings.options.callAutoMessageMaxLength"));
      setTransferTicketMessageText(
        savedTransferTicketMessageText || DEFAULT_TRANSFER_TICKET_MESSAGE_TEXT
      );
      return;
    }
    setLoadingTransferTicketMessageText(true);
    try {
      await update({ key: "transferTicketMessageText", value: toSave });
      setTransferTicketMessageText(toSave);
      setSavedTransferTicketMessageText(toSave);
      toast.success(i18n.t("settings.success"));
    } catch (e) {
      toast.error(i18n.t("settings.options.callAutoMessageSaveError"));
      setTransferTicketMessageText(
        savedTransferTicketMessageText || DEFAULT_TRANSFER_TICKET_MESSAGE_TEXT
      );
    } finally {
      setLoadingTransferTicketMessageText(false);
    }
  }

  async function persistGreetingOneQueueMessageText(value) {
    const trimmed = String(value ?? "").trim();
    const toSave = trimmed || DEFAULT_SEND_GREETING_ONE_QUEUE_MESSAGE_TEXT;
    if (String(value ?? "").length > 500) {
      toast.error(i18n.t("settings.options.callAutoMessageMaxLength"));
      setGreetingOneQueueMessageText(
        savedGreetingOneQueueMessageText || DEFAULT_SEND_GREETING_ONE_QUEUE_MESSAGE_TEXT
      );
      return;
    }
    setLoadingGreetingOneQueueMessageText(true);
    try {
      await update({ key: "sendGreetingMessageOneQueuesText", value: toSave });
      setGreetingOneQueueMessageText(toSave);
      setSavedGreetingOneQueueMessageText(toSave);
      toast.success(i18n.t("settings.success"));
    } catch (e) {
      toast.error(i18n.t("settings.options.callAutoMessageSaveError"));
      setGreetingOneQueueMessageText(
        savedGreetingOneQueueMessageText || DEFAULT_SEND_GREETING_ONE_QUEUE_MESSAGE_TEXT
      );
    } finally {
      setLoadingGreetingOneQueueMessageText(false);
    }
  }
 


  async function handleChangeTimezone(value) {
    setTimezoneType(value);
    setLoadingTimezoneType(true);
    await update({
      key: "timezone",
      value,
    });
    // ✅ CORRECCIÓN: Guardar en cache específico para campañas
    localStorage.setItem("campaignTimezone", value);
    toast.success(i18n.t("settings.success"));
    setLoadingTimezoneType(false);
  }

  return {
    CheckMsgIsGroup,
    SendGreetingAccepted,
    SettingsTransfTicket,
    callAutoMessage,
    callType,
    chatbotType,
    customerMessageLocale,
    greetingOneQueueMessageText,
    handleCallAutoMessage,
    handleCallType,
    handleChangeTimezone,
    handleChangeUserRating,
    teamsEnabled,
    loadingTeamsEnabled,
    handleChangeTeamsEnabled,
    handleChatbotType,
    handleCustomerMessageLocale,
    handleGroupType,
    handleInternalSignature,
    handleRemoveTagsOnCloseTicket,
    handleReopenTicketToBot,
    handleRestoreDefaultMessage,
    handleRestoreDefaultTicketCloseMessage,
    handleScheduleType,
    handleSendGreetingAccepted,
    handleSendGreetingMessageOneQueues,
    handleSendTicketCloseMessage,
    handleSettingsTransfTicket,
    handleValidateWhatsAppNumbers,
    internalSignature,
    loadingCallAutoMessage,
    loadingCallType,
    loadingChatbotType,
    loadingCheckMsgIsGroup,
    loadingCustomerMessageLocale,
    loadingGreetingOneQueueMessageText,
    loadingInternalSignature,
    loadingRemoveTagsOnCloseTicket,
    loadingReopenTicketToBot,
    loadingReopenTicketToBotMinutes,
    loadingScheduleType,
    loadingSendGreetingAccepted,
    loadingSendGreetingAcceptedMessageText,
    loadingSendGreetingMessageOneQueues,
    loadingSendTicketCloseMessage,
    loadingSettingsTransfTicket,
    loadingTicketCloseMessageText,
    loadingTimezoneType,
    loadingTimezones,
    loadingTransferTicketMessageText,
    loadingUserRating,
    loadingUserRatingMessageText,
    loadingValidateWhatsAppNumbers,
    persistGreetingOneQueueMessageText,
    persistSendGreetingAcceptedMessageText,
    persistTicketCloseMessageText,
    persistTransferTicketMessageText,
    persistUserRatingMessageText,
    removeTagsOnCloseTicket,
    reopenTicketToBot,
    reopenTicketToBotMinutes,
    persistReopenTicketToBotMinutes,
    setReopenTicketToBotMinutes,
    reopenGraceMessageText,
    setReopenGraceMessageText,
    persistReopenGraceMessageText,
    handleRestoreDefaultReopenGraceMessage,
    loadingReopenGraceMessageText,
    savedCallAutoMessage,
    savedGreetingOneQueueMessageText,
    savedSendGreetingAcceptedMessageText,
    savedTicketCloseMessageText,
    savedTransferTicketMessageText,
    savedUserRatingMessageText,
    scheduleType,
    sendGreetingAcceptedMessageText,
    sendGreetingMessageOneQueues,
    sendTicketCloseMessage,
    setCallAutoMessage,
    setCallType,
    setChatbotType,
    setCheckMsgIsGroupType,
    setCustomerMessageLocale,
    setGreetingOneQueueMessageText,
    setInternalSignature,
    setLoadingCallAutoMessage,
    setLoadingCallType,
    setLoadingChatbotType,
    setLoadingCheckMsgIsGroup,
    setLoadingCustomerMessageLocale,
    setLoadingGreetingOneQueueMessageText,
    setLoadingInternalSignature,
    setLoadingRemoveTagsOnCloseTicket,
    setLoadingScheduleType,
    setLoadingSendGreetingAccepted,
    setLoadingSendGreetingAcceptedMessageText,
    setLoadingSendGreetingMessageOneQueues,
    setLoadingSendTicketCloseMessage,
    setLoadingSettingsTransfTicket,
    setLoadingTicketCloseMessageText,
    setLoadingTimezoneType,
    setLoadingTimezones,
    setLoadingTransferTicketMessageText,
    setLoadingUserRating,
    setLoadingUserRatingMessageText,
    setLoadingValidateWhatsAppNumbers,
    setRemoveTagsOnCloseTicket,
    setSavedCallAutoMessage,
    setSavedGreetingOneQueueMessageText,
    setSavedSendGreetingAcceptedMessageText,
    setSavedTicketCloseMessageText,
    setSavedTransferTicketMessageText,
    setSavedUserRatingMessageText,
    setScheduleType,
    setSendGreetingAccepted,
    setSendGreetingAcceptedMessageText,
    setSendGreetingMessageOneQueues,
    setSendTicketCloseMessage,
    setSettingsTransfTicket,
    setTicketCloseMessageText,
    setTimezoneType,
    setTimezones,
    setTransferTicketMessageText,
    setUserRating,
    setUserRatingMessageText,
    setValidateWhatsAppNumbers,
    ticketCloseMessageText,
    timezoneType,
    timezones,
    transferTicketMessageText,
    userRating,
    userRatingMessageText,
    validateWhatsAppNumbers,
  };
}
